import { useState, type UIEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLayout } from "../../contexts/UseLayout";
import ImageGeneric from "@/assets/images/lg.png";
import { Navigate, NavLink, useNavigate } from "react-router-dom";
import { Building2, ShieldCheck } from "lucide-react";

import { UserGoOn } from "./UserGoOn";
import { toast } from "react-toastify";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import LanguageSwitcher from "../LanguageSwitcher";
import EmpresaService from "@/services/empresas";
import TemPermissao from "@/components/utils/NiveisDeAcesso";

const EMPRESAS_PER_PAGE = 10;

export default function MenuLateral() {
  const context = useLayout();
  const { setEmpresaId } = useLayout();
  const userIsAdmin = context?.userLogged?.data?.is_admin;
  const userLogado = context?.userLogged?.data;
  const canManageCompanyProfile =
    userIsAdmin ||
    TemPermissao(context?.userLogged, "empresas", "editar") ||
    TemPermissao(context?.userLogged, "empresas", "visualizar");
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(true);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeSvg, setActiveSvg] = useState<boolean>(false);
  const [showCompanySelector, setShowCompanySelector] =
    useState<boolean>(false);

  // Busca empresas paginadas quando o usuário é admin
  const {
    data: adminEmpresas,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["admin-empresas"],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await EmpresaService.getEmpresas(
        pageParam,
        EMPRESAS_PER_PAGE,
      );
      return response?.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.data || lastPage.data.length < EMPRESAS_PER_PAGE) {
        return undefined;
      }

      return allPages.length + 1;
    },
    initialPageParam: 1,
    retry: 1,
    enabled: !!userIsAdmin,
    staleTime: 1000 * 60 * 5, // 5 minutos
  });

  // Define qual lista de empresas usar (admin ou do usuário normal)
  const empresasList = userIsAdmin
    ? adminEmpresas?.pages?.flatMap((page: any) => page?.data || []) || []
    : userLogado?.empresas || [];

  // Obtém o ID da empresa atualmente selecionada
  const selectedEmpresaId =
    localStorage.getItem("empresaId") || userLogado?.empresa?.id?.toString();
  const selectedEmpresaNome =
    empresasList.find((emp: any) => emp.id.toString() == selectedEmpresaId)
      ?.nome_fantasia ||
    localStorage.getItem("empresaNome") ||
    userLogado?.empresa?.nome_fantasia;

  // Determina se há múltiplas empresas (incluindo páginas ainda não carregadas)
  const hasMultipleEmpresas = userIsAdmin
    ? empresasList.length > 1 || !!hasNextPage
    : (userLogado?.empresas?.length ?? 0) > 1;

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("dados");
    localStorage.removeItem("empresaNome");
    setIsLoggedIn(false);
    toast.success("Deslogado com Sucesso!");
    queryClient.clear(); // Limpa o cache do React Query
  };

  const handleCompanyChange = (empresa: any) => {
    try {
      // Usa a função do contexto para salvar a empresa selecionada
      setEmpresaId(empresa.id.toString());
      localStorage.setItem("empresaNome", empresa.nome_fantasia);
      toast.success(
        `Empresa "${empresa.nome_fantasia}" selecionada com sucesso!`,
      );

      // Fecha o seletor
      setShowCompanySelector(false);

      // Limpa todo o cache para forçar recarregamento dos dados da nova empresa
      queryClient.clear();

      // Redireciona para home sem reload
      navigate("/home", { replace: true });
    } catch (error) {
      toast.error("Erro ao selecionar empresa");
    }
  };

  function handleToggle(value: boolean) {
    setTimeout(() => {
      setActiveSvg(value);
    }, 100);
  }

  const handleCompanySelectorScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 24;

    if (isNearBottom) {
      fetchNextPage();
    }
  };

  return (
    <motion.div
      initial={false}
      animate={{
        width: isExpanded ? "16rem" : "3.5rem",
        minWidth: isExpanded ? "16rem" : "3.5rem",
      }}
      transition={{
        duration: 0.3,
        ease: "easeInOut",
      }}
      onMouseEnter={() => {
        setIsExpanded(true);
        handleToggle(true);
      }}
      onMouseLeave={() => {
        setIsExpanded(false);
        handleToggle(false);
      }}
      className="bg-cinza-100 hidden md:flex flex-col items-center relative"
    >
      <motion.div
        className="w-full bg-white mt-20 rounded-r-[20px] flex flex-col relative"
        animate={{
          paddingLeft: isExpanded ? "0" : "0",
          paddingRight: isExpanded ? "0" : "0",
        }}
      >
        <div
          className={`flex items-center justify-center px-2 ${activeSvg ? "py-6" : "py-2"}`}
        >
          {!!activeSvg ? (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <svg
                width="144"
                height="22"
                viewBox="0 0 144 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18.0064 16.7125H8.74442L9.92591 14.0073H16.8232L11.7434 2.28723L3.47128 21.301H0L8.37498 2.03329C8.67123 1.10746 9.42403 0 10.4278 0H13.0574C14.1657 0 14.7268 1.13568 15.0962 2.03329C20.6796 14.889 17.8879 8.46116 23.4712 21.301H19.9999L18.0064 16.7125Z"
                  fill="black"
                />
                <path
                  d="M25.6641 6.12808C25.6641 2.74926 28.3825 0 31.7057 0H36.3741C42.0603 0 46.1223 4.75433 46.1223 10.3886V10.9124C46.1223 16.5626 42.0603 21.301 36.3741 21.301H25.6658V6.12808H25.6641ZM28.8408 18.52H35.6806C40.0232 18.52 42.8445 14.8873 42.8445 10.6426C42.8445 6.39789 40.0232 2.781 35.6806 2.781H28.8408V18.52Z"
                  fill="black"
                />
                <path
                  d="M58.3984 6.12808C58.3984 2.74926 61.1169 0 64.4401 0H69.1085C74.7947 0 78.8567 4.75433 78.8567 10.3886V10.9124C78.8567 16.5626 74.7947 21.301 69.1085 21.301H58.4002V6.12808H58.3984ZM61.5752 18.52H68.415C72.7575 18.52 75.5788 14.8873 75.5788 10.6426C75.5788 6.39789 72.7575 2.781 68.415 2.781H61.5752V18.52Z"
                  fill="black"
                />
                <path
                  d="M100.178 2.781H92.4375C88.1838 2.781 84.6968 6.32383 84.6968 10.6426C84.6968 14.9613 88.182 18.52 92.4375 18.52H100.842L100.178 21.301H91.7439C86.117 21.301 81.5078 16.6225 81.5078 10.9124V10.3886C81.5078 4.6785 86.117 0 91.7439 0H100.842L100.178 2.781ZM99.4985 12.0481H88.3022L88.9522 9.25296H99.4985V12.0481Z"
                  fill="black"
                />
                <path
                  d="M104.187 18.52H116.048C117.895 18.52 119.4 16.9805 119.4 15.1112C119.4 13.2419 117.879 11.7783 116.048 11.7783H109.49C106.315 11.7783 103.729 9.05722 103.729 5.84416C103.729 2.63111 106.313 0 109.49 0H121.631V2.781H110.051C108.323 2.781 106.906 4.21647 106.906 5.97995C106.906 7.74343 108.338 9.10307 110.051 9.10307H116.624C119.918 9.10307 122.591 11.9141 122.591 15.247C122.591 18.58 119.902 21.301 116.624 21.301H103.523L104.187 18.52Z"
                  fill="black"
                />
                <path
                  d="M136.249 8.2372C135.171 9.52278 134.979 9.58274 134.048 10.1947C135.613 10.7184 135.939 11.0023 136.988 12.496L143.235 21.2993H139.262L132.26 11.4344H127.534V21.2993H124.359V0H127.534V8.95317H131.685L139.292 0H143.251L136.249 8.2372Z"
                  fill="black"
                />
              </svg>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <svg
                width="29"
                height="17"
                viewBox="0 0 29 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.1544 5.71215H2.98875L3.39257 4.78755H5.74998L4.01379 0.781751L1.18645 7.28047H0L2.86248 0.694957C2.96374 0.37852 3.22104 0 3.56411 0H4.46288C4.84168 0 5.03347 0.388163 5.15973 0.694957C7.06806 5.08892 6.1139 2.89194 8.02222 7.28047H6.83577L6.1544 5.71215Z"
                  fill="black"
                />
                <path
                  d="M8.77344 2.09452C8.77344 0.939669 9.70258 0 10.8384 0H12.434C14.3775 0 15.7659 1.62498 15.7659 3.55073V3.72974C15.7659 5.66092 14.3775 7.28047 12.434 7.28047H8.77403V2.09452H8.77344ZM9.85923 6.32996H12.197C13.6812 6.32996 14.6455 5.08832 14.6455 3.63752C14.6455 2.18673 13.6812 0.950518 12.197 0.950518H9.85923V6.32996Z"
                  fill="black"
                />
                <path
                  d="M0 11.114C0 9.9592 0.929146 9.01953 2.06497 9.01953H3.66059C5.60406 9.01953 6.99241 10.6445 6.99241 12.5703V12.7493C6.99241 14.6804 5.60406 16.3 3.66059 16.3H0.000596188V11.114H0ZM1.08579 15.3495H3.42354C4.90779 15.3495 5.87208 14.1078 5.87208 12.6571C5.87208 11.2063 4.90779 9.97005 3.42354 9.97005H1.08579V15.3495Z"
                  fill="black"
                />
                <path
                  d="M14.2798 9.97005H11.6341C10.1802 9.97005 8.9884 11.181 8.9884 12.6571C8.9884 14.1332 10.1796 15.3495 11.6341 15.3495H14.5067L14.2798 16.3H11.397C9.47381 16.3 7.89844 14.7009 7.89844 12.7493V12.5703C7.89844 10.6186 9.47381 9.01953 11.397 9.01953H14.5067L14.2798 9.97005ZM14.0475 13.1374H10.2207L10.4429 12.1821H14.0475V13.1374Z"
                  fill="black"
                />
                <path
                  d="M15.6488 15.3495H19.7025C20.3338 15.3495 20.8484 14.8233 20.8484 14.1844C20.8484 13.5455 20.3285 13.0452 19.7025 13.0452H17.4612C16.376 13.0452 15.4922 12.1152 15.4922 11.017C15.4922 9.91882 16.3754 9.01953 17.4612 9.01953H21.6108V9.97005H17.653C17.0622 9.97005 16.5779 10.4607 16.5779 11.0634C16.5779 11.6662 17.0675 12.1309 17.653 12.1309H19.8996C21.0253 12.1309 21.939 13.0916 21.939 14.2308C21.939 15.37 21.02 16.3 19.8996 16.3H15.4219L15.6488 15.3495Z"
                  fill="black"
                />
                <path
                  d="M26.6107 11.8349C26.242 12.2743 26.1765 12.2948 25.8584 12.504C26.3933 12.683 26.5047 12.78 26.8632 13.2905L28.9985 16.2994H27.6405L25.2474 12.9277H23.6321V16.2994H22.5469V9.01953H23.6321V12.0796H25.0508L27.6506 9.01953H29.0038L26.6107 11.8349Z"
                  fill="black"
                />
              </svg>
            </motion.div>
          )}
        </div>

        {/* Company Name */}
        <AnimatePresence>
          {isExpanded && selectedEmpresaNome && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="px-6 pb-4 relative"
            >
              <div
                className={`bg-[#F5F5F5] rounded-lg px-3 py-2 ${
                  hasMultipleEmpresas
                    ? "cursor-pointer hover:bg-[#ECECEC] transition-colors"
                    : ""
                }`}
                onClick={() => {
                  if (hasMultipleEmpresas) {
                    setShowCompanySelector(!showCompanySelector);
                  }
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] text-[#8C8C8C]">EMPRESA</p>
                  {hasMultipleEmpresas && (
                    <div className="text-[#8C8C8C]">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13 4.99999L10.5 2.49999M13 4.99999L10.5 7.49999M13 4.99999H5.5C3.01472 4.99999 1 7.0147 1 9.49999V9.99999M1 8.99999L3.5 11.5M1 8.99999L3.5 6.49999M1 8.99999H8.5C10.9853 8.99999 13 6.9853 13 4.49999V3.99999"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <p
                  className="text-xs font-medium text-[#1E1E1E] truncate"
                  title={selectedEmpresaNome}
                >
                  {selectedEmpresaNome}
                </p>
              </div>

              {/* Company Selector Dropdown */}
              <AnimatePresence>
                {showCompanySelector && hasMultipleEmpresas && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    onScroll={handleCompanySelectorScroll}
                    className="absolute top-full left-6 right-6 mt-1 bg-white rounded-lg shadow-lg border border-[#E5E5E5] z-50 max-h-60 overflow-y-auto"
                  >
                    {empresasList.map((empresa: any) => (
                      <button
                        key={empresa.id}
                        type="button"
                        onClick={() => handleCompanyChange(empresa)}
                        className={`w-full text-left px-3 py-2 hover:bg-[#F5F5F5] transition-colors first:rounded-t-lg last:rounded-b-lg ${
                          empresa.id.toString() === selectedEmpresaId
                            ? "bg-[#F5F5F5]"
                            : ""
                        }`}
                      >
                        <p
                          className="text-xs font-medium text-[#1E1E1E] truncate"
                          title={empresa.nome_fantasia}
                        >
                          {empresa.nome_fantasia}
                        </p>
                        <p className="text-[10px] text-[#8C8C8C] truncate">
                          {empresa.cnpj}
                        </p>
                      </button>
                    ))}
                    {isFetchingNextPage && (
                      <div className="px-3 py-2 text-[10px] text-[#8C8C8C] text-center">
                        Carregando mais empresas...
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Language Switcher */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute -top-3 right-0"
            >
              <LanguageSwitcher />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Menu */}
        <nav className="font-medium">
          <ul className="text-cinza-200 px-2 flex flex-col gap-1 text-sm">
            <UserGoOn is_admin={userIsAdmin} isExpanded={isExpanded} />
          </ul>
        </nav>
        <div className="text-cinza-200 px-2 flex flex-col gap-1 text-sm pt-3">
          <div className="mb-3">
            {!!isExpanded ? (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
                className="pl-7"
              >
                <p className="text-[#8C8C8C] text-[10px] text-left">CONTA</p>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
              >
                <div className="w-[35px] h-[1px] bg-[linear-gradient(90deg,rgba(210,210,210,0)_0%,rgba(210,210,210,1)_50%,rgba(210,210,210,0)_100%)]"></div>
              </motion.div>
            )}
          </div>
          <NavLink
            to="/editar-usuario"
            className={({ isActive, isPending }) =>
              isPending
                ? ""
                : isActive
                  ? "text-black"
                  : "hover:text-black transition-all"
            }
          >
            <li
              className={`flex items-center py-2 duration-300 gap-3 ${isExpanded ? "px-6" : "px-2"}`}
            >
              <div className=" flex justify-center items-center">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 12 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.5 14.5C11.5 11.4931 9.03757 9.05556 6 9.05556C2.96243 9.05556 0.5 11.4931 0.5 14.5M6 6.72222C4.26425 6.72222 2.85714 5.32933 2.85714 3.61111C2.85714 1.89289 4.26425 0.5 6 0.5C7.73575 0.5 9.14286 1.89289 9.14286 3.61111C9.14286 5.32933 7.73575 6.72222 6 6.72222Z"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
              <AnimatePresence>
                {isExpanded && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: "auto" }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.2 }}
                    className="whitespace-nowrap overflow-hidden text-xs"
                  >
                    Perfil
                  </motion.span>
                )}
              </AnimatePresence>
            </li>
          </NavLink>

          {canManageCompanyProfile && (
            <NavLink
              to="/perfil-empresa"
              className={({ isActive, isPending }) =>
                isPending
                  ? ""
                  : isActive
                    ? "text-black"
                    : "hover:text-black transition-all"
              }
            >
              <li
                className={`flex items-center py-2 duration-300 gap-3 ${isExpanded ? "px-6" : "px-2"}`}
              >
                <div className="flex justify-center items-center">
                  <Building2 size={20} />
                </div>
                <AnimatePresence>
                  {isExpanded && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: "auto" }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2 }}
                      className="whitespace-nowrap overflow-hidden text-xs"
                    >
                      Perfil da empresa
                    </motion.span>
                  )}
                </AnimatePresence>
              </li>
            </NavLink>
          )}

          <NavLink
            to="/perfil/seguranca"
            className={({ isActive, isPending }) =>
              isPending
                ? ""
                : isActive
                  ? "text-black"
                  : "hover:text-black transition-all"
            }
          >
            <li
              className={`flex items-center py-2 duration-300 gap-3 ${isExpanded ? "px-6" : "px-2"}`}
            >
              <div className="flex justify-center items-center">
                <ShieldCheck size={20} />
              </div>
              <AnimatePresence>
                {isExpanded && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: "auto" }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.2 }}
                    className="whitespace-nowrap overflow-hidden text-xs"
                  >
                    Segurança
                  </motion.span>
                )}
              </AnimatePresence>
            </li>
          </NavLink>
        </div>

        {/* User Profile Section */}
        <div
          className={`mt-2 flex justify-between items-center py-5 transition-all duration-300 gap-1 ${isExpanded ? "px-6" : "px-2"}`}
        >
          <div className="flex items-center gap-1">
            <motion.img
              className="rounded-full object-cover bg-[#F5F5F5] min-w-[1.9rem] flex justify-between gap-3 items-center"
              src={userLogado?.filePath ? userLogado?.filePath : ImageGeneric}
              alt={userLogado?.name}
              animate={{
                width: isExpanded ? "1.9rem" : "1.9rem",
                height: isExpanded ? "1.9rem" : "1.9rem",
              }}
              transition={{ duration: 0.3 }}
            />

            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col overflow-hidden"
                >
                  <span className="text-xs font-medium text-[#1E1E1E]">
                    {userLogado?.name}
                  </span>
                  <span className="text-[#D2D2D2] text-[10px]">
                    {userLogado?.cargoNome}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col overflow-hidden"
              >
                <button
                  type="button"
                  title="Sair"
                  onClick={handleLogout}
                  className="px-4 py-2 text-cinza-300 hover:text-black duration-300 flex items-center gap-4 w-full"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 9.4375L9.4375 7M9.4375 7L7 4.5625M9.4375 7H0.5M4.5625 3.13948V3.10016C4.5625 2.19007 4.5625 1.73469 4.73961 1.38708C4.89541 1.08132 5.14382 0.832909 5.44958 0.677114C5.79719 0.5 6.25257 0.5 7.16266 0.5H10.9002C11.8102 0.5 12.2647 0.5 12.6123 0.677114C12.918 0.832909 13.1673 1.08132 13.3231 1.38708C13.5 1.73435 13.5 2.18918 13.5 3.09749V10.9029C13.5 11.8112 13.5 12.2654 13.3231 12.6127C13.1673 12.9184 12.918 13.1673 12.6123 13.3231C12.265 13.5 11.8108 13.5 10.9025 13.5H7.15999C6.25168 13.5 5.79685 13.5 5.44958 13.3231C5.14382 13.1673 4.89541 12.9182 4.73961 12.6124C4.5625 12.2648 4.5625 11.8101 4.5625 10.9V10.8594"
                      stroke="#8C8C8C"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  {!isLoggedIn && <Navigate to="/login" />}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}
